

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { projects } from '../data/projects';
import GidiIcon from '../components/GidiIcon';
import ImageWithLoader from '../components/ImageWithLoader';
import './Work.css';

// Define constants outside component to prevent recreation
const MIT_TAGS = ['Interaction Design', 'Fabrication', 'Accessibility'];

// Calculate all tags once since projects data is static
const ALL_TAGS = Array.from(new Set<string>(
  projects.flatMap(p => p.tags || [])
)).sort();

// Duration constants - easily adjustable
const FLIP_DURATION = 1000; // 1 second for the flip animation
const HOLD_DURATION = 5000; // 3 seconds to stay flipped
const INTERVAL_DURATION = 4000; // 4 seconds between flips
const INITIAL_DELAY = 100; // 2 seconds before first flip

const Work: React.FC = () => {
  const navigate = useNavigate();
  const { mitMode } = useApp();
  const [isCompact, setIsCompact] = useState(false);
  // Toggle to show/hide the chip filter list at the top
  const [showChipFilters, setShowChipFilters] = useState<boolean>(true);
  
  // Initialize selectedTags based on mitMode
  const [selectedTags, setSelectedTags] = useState<string[]>(() => 
    mitMode ? ALL_TAGS.filter(tag => MIT_TAGS.includes(tag)) : ALL_TAGS
  );

  // Sync selectedTags when mitMode changes
  useEffect(() => {
    setSelectedTags(mitMode ? ALL_TAGS.filter(tag => MIT_TAGS.includes(tag)) : ALL_TAGS);
  }, [mitMode]);

  // Filter projects by selected tags
  const filteredProjects = useMemo(() => {
    if (selectedTags.length === 0) return [];
    return projects.filter(p =>
      p.tags?.some(tag => selectedTags.includes(tag))
    );
  }, [selectedTags]);
  // Toggle tag selection
  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };
  
  // Static boolean to enable/disable the flipping effect
  const FLIP_EFFECT_ENABLED = true;
  
  // State for tracking which cards are currently flipped (3 at a time)
  const [flippedCardIndices, setFlippedCardIndices] = useState<number[]>([]);
  const [flipDirections, setFlipDirections] = useState<string[]>([]);
  const [cardOrder, setCardOrder] = useState<number[]>([]);
  const [flipAxes, setFlipAxes] = useState<('x' | 'y')[]>([]); // Track which axis each card is flipping on

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Handle scroll to make navigation compact
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsCompact(scrollTop > 100); // Make compact after scrolling 100px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize random card order
  useEffect(() => {
    if (!FLIP_EFFECT_ENABLED) return;
    
    // Create a random order of all cards
    const shuffledOrder = projects
      .map((_, index) => index)
      .sort(() => Math.random() - 0.5);
    
    setCardOrder(shuffledOrder);
  }, [FLIP_EFFECT_ENABLED]);

  // Random card flipping effect
  useEffect(() => {
    if (!FLIP_EFFECT_ENABLED || cardOrder.length === 0) return;

    const startFlipping = (orderIndex: number) => {
      // Get 3 cards that are spaced apart when possible
      const cardIndex1 = cardOrder[orderIndex];
      const cardIndex2 = cardOrder[(orderIndex + Math.floor(cardOrder.length / 3)) % cardOrder.length];
      const cardIndex3 = cardOrder[(orderIndex + Math.floor(2 * cardOrder.length / 3)) % cardOrder.length];
      
      // Select random rotation directions and axes for all three cards
      const flipOptions = [
        'rotateX(180deg)',
        'rotateX(-180deg)', 
        'rotateY(180deg)',
        'rotateY(-180deg)'
      ];
      const randomFlip1 = flipOptions[Math.floor(Math.random() * flipOptions.length)];
      const randomFlip2 = flipOptions[Math.floor(Math.random() * flipOptions.length)];
      const randomFlip3 = flipOptions[Math.floor(Math.random() * flipOptions.length)];
      
      // Set the flip axes for all three cards
      const flipAxis1 = randomFlip1.includes('rotateX') ? 'x' : 'y';
      const flipAxis2 = randomFlip2.includes('rotateX') ? 'x' : 'y';
      const flipAxis3 = randomFlip3.includes('rotateX') ? 'x' : 'y';
      
      // Start first card immediately
      setFlipDirections([randomFlip1, '', '']);
      setFlippedCardIndices([cardIndex1, -1, -1]);
      setFlipAxes([flipAxis1, 'y', 'y']);
      
      // Start second card after one-third interval
      setTimeout(() => {
        setFlipDirections([randomFlip1, randomFlip2, '']);
        setFlippedCardIndices([cardIndex1, cardIndex2, -1]);
        setFlipAxes([flipAxis1, flipAxis2, 'y']);
        
        // Start third card after two-thirds interval
        setTimeout(() => {
          setFlipDirections([randomFlip1, randomFlip2, randomFlip3]);
          setFlippedCardIndices([cardIndex1, cardIndex2, cardIndex3]);
          setFlipAxes([flipAxis1, flipAxis2, flipAxis3]);
          
          // Keep all three flipped for HOLD_DURATION, then flip back with staggered timing
          setTimeout(() => {
            // Continue rotating in the same direction to return to front
            const returnDirection1 = randomFlip1.includes('rotateX') 
              ? randomFlip1.includes('-') ? 'rotateX(-360deg)' : 'rotateX(360deg)'
              : randomFlip1.includes('-') ? 'rotateY(-360deg)' : 'rotateY(360deg)';
            const returnDirection2 = randomFlip2.includes('rotateX') 
              ? randomFlip2.includes('-') ? 'rotateX(-360deg)' : 'rotateX(360deg)'
              : randomFlip2.includes('-') ? 'rotateY(-360deg)' : 'rotateY(360deg)';
            const returnDirection3 = randomFlip3.includes('rotateX') 
              ? randomFlip3.includes('-') ? 'rotateX(-360deg)' : 'rotateX(360deg)'
              : randomFlip3.includes('-') ? 'rotateY(-360deg)' : 'rotateY(360deg)';
            
            // Start first card returning immediately
            setFlipDirections([returnDirection1, randomFlip2, randomFlip3]);
            
            // Start second card returning after one-third interval
            setTimeout(() => {
              setFlipDirections([returnDirection1, returnDirection2, randomFlip3]);
              
              // Start third card returning after two-thirds interval
              setTimeout(() => {
                setFlipDirections([returnDirection1, returnDirection2, returnDirection3]);
                
                // After the return animation completes, reset to no transform and continue to next cards
                setTimeout(() => {
                  setFlippedCardIndices([]);
                  setFlipDirections([]); // Remove transform entirely
                  
                  // Calculate next index (skip 1 card since we're using spaced cards)
                  const nextIndex = (orderIndex + 1) % cardOrder.length;
                  
                  // After flipping back, wait INITIAL_DELAY then start next cards
                  setTimeout(() => {
                    startFlipping(nextIndex);
                  }, INITIAL_DELAY);
                }, FLIP_DURATION);
              }, INTERVAL_DURATION / 3); // One-third interval offset for third card return
            }, INTERVAL_DURATION / 3); // One-third interval offset for second card return
          }, HOLD_DURATION);
        }, INTERVAL_DURATION / 3); // One-third interval offset for third card
      }, INTERVAL_DURATION / 3); // One-third interval offset for second card
    };

    // Start the first flip after INITIAL_DELAY
    const initialDelay = setTimeout(() => startFlipping(0), INITIAL_DELAY);

    return () => {
      clearTimeout(initialDelay);
    };
  }, [FLIP_EFFECT_ENABLED, cardOrder]);

  return (
    <div className="work work-page">
      <nav className={`nav ${isCompact ? 'compact' : ''}`}>
        <Link to="/" className="nav-link-special">
          <GidiIcon size={27} />
        </Link>
        <Link to="/about" className="nav-link">about</Link>
      </nav>
      
      <div className="work-content">
        {/* Tag chip filters */}
        <div className="chip-filter-bar">
          <div className={`chip-filter-list ${showChipFilters ? '' : 'hidden'}`}>
            {ALL_TAGS.map(tag => (
              <button
                key={tag}
                className={`chip-filter${selectedTags.includes(tag) ? ' selected' : ''}`}
                onClick={e => {
                  e.stopPropagation();
                  toggleTag(tag);
                }}
                type="button"
                aria-pressed={selectedTags.includes(tag)}
              >
                {tag}
              </button>
            ))}
            {ALL_TAGS.length > 0 && (
              <button
                className="chip-filter chip-filter-toggleall"
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  if (selectedTags.length === ALL_TAGS.length) {
                    setSelectedTags([]);
                  } else {
                    setSelectedTags(ALL_TAGS);
                  }
                }}
                aria-label={selectedTags.length === ALL_TAGS.length ? 'Deselect all tags' : 'Select all tags'}
              >
                {selectedTags.length === ALL_TAGS.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          {/* Toggle show/hide chip-filter list */}
          <button
            className={`chip-filter-toggle-tags ${showChipFilters ? 'active' : ''}`}
            type="button"
            onClick={e => {
              e.stopPropagation();
              setShowChipFilters(prev => !prev);
            }}
            aria-pressed={showChipFilters}
            title={showChipFilters ? 'Hide tag filters' : 'Show tag filters'}
            aria-label={showChipFilters ? 'Hide tag filters' : 'Show tag filters'}
          >
            {/* Reversed logic: show eye-off (hidden) when list is visible, eye when hidden */}
            {showChipFilters ? (
              <>
                {/* eye-off (hidden) icon with pupil that will be cropped by the slash */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-5 0-9.27-3.11-11-7a16.06 16.06 0 0 1 4.05-5.13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  {/* small filled pupil which will be visually cropped by the slash */}
                  <circle cx="12" cy="12" r="2" fill="currentColor" />
                  <path d="M1 1l22 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </>
            ) : (
              <>
                {/* eye (open) icon with label 'show tags' next to it */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="3" fill="currentColor" />
                </svg>
                <span className="chip-filter-toggle-label">show categories</span>
              </>
            )}
          </button>
        </div>
        
        <div className="projects-grid">
          {filteredProjects.map((project, index) => (
            <div 
              key={project.id} 
              className="project-scene"
              onClick={() => navigate(`/projects/${project.markdownFile}`)}
            >
              <div 
                className={`project-card ${
                  flippedCardIndices.includes(index) 
                    ? flipAxes[flippedCardIndices.indexOf(index)] === 'x'
                      ? 'is-flipped-x' 
                      : 'is-flipped' 
                    : ''
                }`}
                style={flippedCardIndices.includes(index) ? { 
                  transform: flipDirections[flippedCardIndices.indexOf(index)],
                  transition: `transform ${FLIP_DURATION}ms ease-in-out`
                } : {
                  transform: 'none',
                  transition: 'none'
                }}
                data-title={project.title}
                data-description={project.description}
              >
                {/* Front face */}
                <div className="project-card__face project-card__face--front">
                  <ImageWithLoader 
                    src={project.coverImage} 
                    alt={project.title}
                    className="project-img"
                    loaderSize={40}
                  />
                  <div className="project-overlay">
                    <h3 className="project-overlay-title">{project.title}</h3>
                    <p className="project-overlay-description">{project.description}</p>
                    {project.tags && project.tags.length > 0 && (
                      <div className="tag-list tag-list-mobile" aria-label="Project tags">
                        {project.tags.map((tag) => (
                          <span className="tag-pill tag-pill-overlay" key={tag}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Back face with inverted colors - Y axis */}
                <div className="project-card__face project-card__face--back project-card__face--back-y">
                  <div className="project-overlay-inverted">
                    <h3 className="project-overlay-title-inverted">{project.title}</h3>
                    <p className="project-overlay-description-inverted">{project.description}</p>
                    {project.tags && project.tags.length > 0 && (
                      <div className="tag-list tag-list-mobile" aria-label="Project tags">
                        {project.tags.map((tag) => (
                          <span className="tag-pill tag-pill-overlay" key={tag}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Back face with inverted colors - X axis */}
                <div className="project-card__face project-card__face--back project-card__face--back-x">
                  <div className="project-overlay-inverted">
                    <h3 className="project-overlay-title-inverted">{project.title}</h3>
                    <p className="project-overlay-description-inverted">{project.description}</p>
                    {project.tags && project.tags.length > 0 && (
                      <div className="tag-list tag-list-mobile" aria-label="Project tags">
                        {project.tags.map((tag) => (
                          <span className="tag-pill tag-pill-overlay" key={tag}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Work;
